import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Select, Radio, Card, Icon, Spin, Row, Col, InputNumber, Tag, Tooltip, Button } from 'antd';

import { opcoesDeFiltro, previewPublico } from '../api';
import { linhasDoFunil, formataDuracao, SOURCE_LABEL } from '../helpers';

const Option = Select.Option;

const SITUACAO = {
  ativos: 'Clientes ativos',
  inativos: 'Clientes inativos',
  todos: 'Ativos e inativos',
};

const rotulo = (texto, ajuda) => (
  <div style={{ fontSize: 12, color: '#595959', marginBottom: 5, fontWeight: 500 }}>
    {texto}
    {ajuda && (
      <Tooltip title={ajuda}>
        <Icon type="question-circle" style={{ color: '#bfbfbf', marginLeft: 5 }} />
      </Tooltip>
    )}
  </div>
);

/**
 * O funil, como subtração.
 *
 * A pergunta que esta tela responde não é "quantos vão receber" — é "por que
 * não são todos". Um número solto obriga o usuário a descobrir a diferença no
 * relatório, quando não há mais o que fazer.
 */
const Funil = ({ funil, previsao, calculando }) => {
  if (calculando && !funil) {
    return (
      <Card size="small" style={{ borderRadius: 8 }}>
        <div style={{ textAlign: 'center', padding: '14px 0', color: '#8c8c8c' }}>
          <Spin size="small" /> <span style={{ marginLeft: 8 }}>calculando o público…</span>
        </div>
      </Card>
    );
  }

  if (!funil) {
    return (
      <Card size="small" style={{ borderRadius: 8 }}>
        <span style={{ color: '#8c8c8c' }}>Escolha os filtros para ver o público.</span>
      </Card>
    );
  }

  const perdas = linhasDoFunil(funil);
  const totalPerdido = perdas.reduce((soma, p) => soma + p.valor, 0);
  const base = funil.receberao + totalPerdido;
  const proporcao = base ? Math.round((funil.receberao / base) * 100) : 0;
  const vazio = !funil.receberao;

  return (
    <Card
      size="small"
      style={{
        borderRadius: 8,
        borderColor: vazio ? '#e8e8e8' : '#b7eb8f',
        background: vazio ? '#fafafa' : '#f6ffed',
        opacity: calculando ? 0.6 : 1,
        transition: 'opacity .2s',
      }}
    >
      <Row gutter={20} type="flex" align="middle">
        <Col xs={24} md={9}>
          <div style={{
            fontSize: 34, fontWeight: 600, lineHeight: 1.1,
            color: vazio ? '#8c8c8c' : '#237804',
          }}>
            {funil.receberao}
          </div>
          <div style={{ fontSize: 14, color: vazio ? '#8c8c8c' : '#237804', fontWeight: 500 }}>
            {funil.receberao === 1 ? 'pessoa vai receber' : 'pessoas vão receber'}
          </div>

          {base > 0 && (
            <div style={{
              height: 6, borderRadius: 3, background: '#e8e8e8',
              marginTop: 10, overflow: 'hidden',
            }}>
              <div style={{ width: proporcao + '%', height: '100%', background: '#52c41a' }} />
            </div>
          )}

          {!vazio && (
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8 }}>
              <Icon type="clock-circle" /> leva cerca de {formataDuracao(previsao)} — saem 60 por
              minuto para preservar a reputação do domínio.
            </div>
          )}
        </Col>

        <Col xs={24} md={15}>
          <div style={{
            background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8,
            padding: '10px 14px', fontSize: 13,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 6 }}>
              <span style={{ color: '#595959' }}>Clientes no filtro</span>
              <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{funil.clientes}</strong>
            </div>

            {perdas.map((p) => (
              <div
                key={p.chave}
                style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '4px 0', color: '#8c8c8c',
                }}
              >
                <span>
                  <span style={{
                    display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                    background: p.cor, marginRight: 8, verticalAlign: 'middle',
                  }} />
                  {p.rotulo}
                  <Tooltip title={p.ajuda}>
                    <Icon type="question-circle" style={{ color: '#d9d9d9', marginLeft: 5 }} />
                  </Tooltip>
                </span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>−{p.valor}</span>
              </div>
            ))}

            <div style={{
              display: 'flex', justifyContent: 'space-between',
              borderTop: '1px solid #e8e8e8', marginTop: 6, paddingTop: 7,
            }}>
              <span style={{ color: '#237804', fontWeight: 500 }}>Recebem</span>
              <strong style={{ color: '#237804', fontVariantNumeric: 'tabular-nums' }}>
                {funil.receberao}
              </strong>
            </div>
          </div>

          {perdas.length === 0 && !vazio && (
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
              Todo mundo do filtro tem endereço válido — nada foi descartado.
            </div>
          )}
        </Col>
      </Row>
    </Card>
  );
};

/**
 * Passo 1 — quem recebe.
 */
const PassoPublico = ({ filtros, source, onChange }) => {
  const [opcoes, setOpcoes] = useState({ regimes: [], atividades: [], ufs: [] });
  const [funil, setFunil] = useState(null);
  const [previsao, setPrevisao] = useState(0);
  const [calculando, setCalculando] = useState(false);
  const debounce = useRef(null);
  const montado = useRef(true);
  useEffect(() => () => { montado.current = false; }, []);

  useEffect(() => {
    opcoesDeFiltro().then((dados) => dados && setOpcoes(dados));
  }, []);

  const recalcular = useCallback((filtrosAtuais, fonteAtual) => {
    setCalculando(true);
    previewPublico(filtrosAtuais, fonteAtual)
      .then((dados) => {
        if (!montado.current || !dados) return;
        setFunil(dados.funil);
        setPrevisao(dados.previsao_minutos);
      })
      .finally(() => montado.current && setCalculando(false));
  }, []);

  // Espera o usuário parar de mexer antes de consultar: cada recálculo varre a
  // base inteira, e disparar isso a cada clique num multi-select é desperdício.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => recalcular(filtros, source), 400);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [filtros, source, recalcular]);

  const set = (campo, valor) => onChange({ ...filtros, [campo]: valor }, source);

  // Chips do recorte ativo: sem eles o usuário rola a tela para lembrar o que
  // filtrou, e o número grande fica sem contexto.
  const chips = [];
  (filtros.regimes || []).forEach((r) => chips.push({ campo: 'regimes', valor: r, texto: r }));
  (filtros.atividades || []).forEach((a) => chips.push({ campo: 'atividades', valor: a, texto: a }));
  (filtros.uf || []).forEach((u) => chips.push({ campo: 'uf', valor: u, texto: u }));
  if (filtros.honorario_min) chips.push({ campo: 'honorario_min', texto: 'honorário ≥ ' + filtros.honorario_min });
  if (filtros.honorario_max) chips.push({ campo: 'honorario_max', texto: 'honorário ≤ ' + filtros.honorario_max });

  const tirarChip = (chip) => {
    if (chip.valor === undefined) return set(chip.campo, null);
    set(chip.campo, (filtros[chip.campo] || []).filter((v) => v !== chip.valor));
  };

  const limparTudo = () =>
    onChange({ situacao: filtros.situacao || 'ativos' }, source);

  return (
    <div>
      <Card
        size="small"
        title={<span style={{ fontSize: 14 }}>Filtrar a base</span>}
        style={{ marginBottom: 14, borderRadius: 8 }}
        extra={
          chips.length > 0 && (
            <Button size="small" type="link" onClick={limparTudo}>Limpar filtros</Button>
          )
        }
      >
        <Row gutter={12}>
          <Col xs={24} md={8}>
            {rotulo('Situação')}
            <Select
              value={filtros.situacao || 'ativos'}
              onChange={(v) => set('situacao', v)}
              style={{ width: '100%' }}
            >
              {Object.keys(SITUACAO).map((k) => <Option key={k} value={k}>{SITUACAO[k]}</Option>)}
            </Select>
          </Col>
          <Col xs={24} md={8}>
            {rotulo('Regime tributário')}
            <Select
              mode="multiple" placeholder="todos" style={{ width: '100%' }} maxTagCount={2}
              value={filtros.regimes || []}
              onChange={(v) => set('regimes', v)}
            >
              {opcoes.regimes.map((r) => <Option key={r} value={r}>{r}</Option>)}
            </Select>
          </Col>
          <Col xs={24} md={8}>
            {rotulo('Atividade')}
            <Select
              mode="multiple" placeholder="todas" style={{ width: '100%' }} maxTagCount={2}
              value={filtros.atividades || []}
              onChange={(v) => set('atividades', v)}
            >
              {opcoes.atividades.map((a) => <Option key={a} value={a}>{a}</Option>)}
            </Select>
          </Col>
        </Row>

        <Row gutter={12} style={{ marginTop: 12 }}>
          <Col xs={24} md={8}>
            {rotulo('Estado', 'A base guarda "SP" e "SÃO PAULO" como valores diferentes; aqui os dois caem na mesma sigla.')}
            <Select
              mode="multiple" placeholder="todos" style={{ width: '100%' }} maxTagCount={4}
              value={filtros.uf || []}
              onChange={(v) => set('uf', v)}
            >
              {opcoes.ufs.map((u) => <Option key={u} value={u}>{u}</Option>)}
            </Select>
          </Col>
          <Col xs={12} md={8}>
            {rotulo('Honorário mínimo')}
            <InputNumber
              style={{ width: '100%' }} min={0} step={100} placeholder="qualquer"
              value={filtros.honorario_min}
              onChange={(v) => set('honorario_min', v)}
            />
          </Col>
          <Col xs={12} md={8}>
            {rotulo('Honorário máximo')}
            <InputNumber
              style={{ width: '100%' }} min={0} step={100} placeholder="qualquer"
              value={filtros.honorario_max}
              onChange={(v) => set('honorario_max', v)}
            />
          </Col>
        </Row>

        {chips.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed #e8e8e8' }}>
            <span style={{ fontSize: 12, color: '#8c8c8c', marginRight: 8 }}>Recorte:</span>
            <Tag color="blue" style={{ marginBottom: 4 }}>{SITUACAO[filtros.situacao || 'ativos']}</Tag>
            {chips.map((chip, i) => (
              <Tag key={i} closable style={{ marginBottom: 4 }} onClose={() => tirarChip(chip)}>
                {chip.texto}
              </Tag>
            ))}
          </div>
        )}
      </Card>

      <Card
        size="small"
        title={<span style={{ fontSize: 14 }}>Enviar para o e-mail de</span>}
        style={{ marginBottom: 14, borderRadius: 8 }}
      >
        <Radio.Group
          value={source || 'empresa'}
          onChange={(e) => onChange(filtros, e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="empresa">{SOURCE_LABEL.empresa}</Radio.Button>
          <Radio.Button value="painel">{SOURCE_LABEL.painel}</Radio.Button>
          <Radio.Button value="ambos">{SOURCE_LABEL.ambos}</Radio.Button>
        </Radio.Group>

        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8 }}>
          Um mesmo endereço pode servir várias empresas — quem aparece nas duas
          origens recebe uma vez só.
        </div>
      </Card>

      <Funil funil={funil} previsao={previsao} calculando={calculando} />
    </div>
  );
};

export default PassoPublico;
